<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { DateTime } from 'luxon';
import type { DateCopy, DateElements } from '@/modules/common/types/ui.ts';
import TodayIcon from '@/assets/icons/shared/today.svg';
import CopyIcon from '@/assets/icons/shared/copy.svg';
import DateRangePicker from 'vue3-daterange-picker';
import { ALL_DATE_ELEMENTS } from '@/modules/common/consts/date.ts';
import CalendarIcon from '@/assets/icons/ui/calendar.svg';
import CalendarPlusIcon from '@/assets/icons/ui/calendar-plus.svg';
import ChevronRightIcon from '@/assets/icons/shared/chevron.svg';
import InputButton from '@/modules/ui/components/buttons/InputButton.vue';
import type { PickerDateRange } from '@/modules/common/types/luxon.type.ts';
import { useDatePickerLocale } from '@/modules/common/composables/use-date-picker-locale.ts';
import UiButton from '@/modules/ui/components/ui/UiButton.vue';
import UiInput from '@/modules/ui/components/ui/UiInput.vue';
import UiTooltip from '@/modules/ui/components/ui/UiTooltip.vue';

enum Format {
  Date = 'dd.MM.yyyy',
  DateWeekDay = 'dd.MM.yyyy, ccc',
}

const date = defineModel<DateTime>();

const emit = defineEmits<{
  pickerChange: [date: DateTime];
}>();

const {
  required,
  disabled,
  readonly,
  placeholder = '__.__.____',
  elements = ALL_DATE_ELEMENTS,
  min,
  max,
  copy,
  calendarAvailable,
  drops = 'down',
} = defineProps<{
  error?: boolean;
  placeholder?: string;
  readonly?: boolean | 'formatted';
  disabled?: boolean;
  required?: boolean;
  elements?: DateElements;
  min?: DateTime;
  max?: DateTime;
  copy?: DateCopy;
  calendarAvailable?: boolean;
  drops?: 'down' | 'up';
}>();

const input = ref('');

const isDecreaseDisabled = computed(
  () => disabled || (date.value && min ? date.value.startOf('day') <= min.startOf('day') : false)
);
const isIncreaseDisabled = computed(
  () => disabled || (date.value && max ? date.value.startOf('day') >= max.startOf('day') : false)
);

const isCopyValid = computed(() => !!copy && (!min || copy.date >= min) && (!max || copy.date <= max));

const minJs = computed(() => min?.toJSDate());
const maxJs = computed(() => max?.toJSDate());

const dateRange = computed<PickerDateRange>(() => ({
  startDate: date.value?.toJSDate() || null,
  endDate: date.value?.toJSDate() || null,
}));

const dateRangePickerAvailable = computed(() => !!readonly && !calendarAvailable);

const localeData = useDatePickerLocale();

// TODO: Учитывать локаль
function getFormat() {
  return readonly === 'formatted' ? Format.DateWeekDay : Format.Date;
}

function updateInput(dt: DateTime | undefined) {
  input.value = dt ? dt?.toFormat(getFormat()) : '';
}

watch(date, updateInput, { immediate: true });
watch(
  () => readonly,
  () => updateInput(date.value)
);

function onPickerChange(dateRange: PickerDateRange) {
  date.value = dateRange.startDate ? DateTime.fromJSDate(dateRange.startDate) : undefined;

  if (dateRange.endDate) {
    emit('pickerChange', DateTime.fromJSDate(dateRange.endDate));
  }
}

function onInputBlur() {
  const dt = DateTime.fromFormat(input.value, getFormat());

  if (dt.isValid) {
    date.value = dt;
  } else if (!input.value && !required) {
    date.value = undefined;
  } else {
    updateInput(date.value);
  }
}

function decrease() {
  date.value = date.value ? date.value.minus({ days: 1 }) : min || DateTime.now();
}

function increase() {
  date.value = date.value ? date.value.plus({ days: 1 }) : max || DateTime.now();
}

function setToday() {
  const now = DateTime.now();
  date.value = (min && DateTime.max(now, min)) || (max && DateTime.min(now, max)) || now;
}

function setCopy() {
  date.value = copy?.date;
}
</script>

<template>
  <div
    class="ui-date"
    :class="[`_drops-${drops}`, { '_picker-input': elements.calendar && elements.input && readonly }]"
  >
    <UiInput
      v-if="elements.input && !readonly"
      v-model="input"
      align="center"
      autocomplete="off"
      mask="##.##.####"
      :placeholder="placeholder"
      :disabled="disabled"
      :error
      no-clear
      @blur="onInputBlur"
    >
      <template #prefix>
        <InputButton
          v-if="!readonly"
          :disabled="isDecreaseDisabled"
          tabindex="-1"
          :error
          @click="decrease"
        >
          <ChevronRightIcon class="chevron-icon _left" />
        </InputButton>
      </template>

      <template #postfix>
        <InputButton
          v-if="!readonly"
          :disabled="isIncreaseDisabled"
          tabindex="-1"
          :error
          side="right"
          @click="increase"
        >
          <ChevronRightIcon class="chevron-icon _right" />
        </InputButton>
      </template>
    </UiInput>

    <UiButton
      v-if="elements.today && !readonly"
      mode="outline"
      color="black"
      shape="square"
      :disabled="disabled"
      tabindex="-1"
      @click="setToday"
    >
      <TodayIcon />
    </UiButton>

    <UiTooltip
      v-if="elements.copy && !readonly && isCopyValid"
      placement="top"
      :text="copy?.tooltip"
      :disabled="disabled"
    >
      <UiButton
        mode="outline"
        color="black"
        shape="square"
        :disabled="disabled"
        tabindex="-1"
        @click="setCopy"
      >
        <CopyIcon />
      </UiButton>
    </UiTooltip>

    <DateRangePicker
      v-if="elements.calendar"
      :date-range="dateRange"
      :single-date-picker="true"
      :ranges="false"
      :locale-data="localeData"
      :min-date="minJs"
      :max-date="maxJs"
      :disabled="disabled"
      :readonly="dateRangePickerAvailable"
      opens="left"
      auto-apply
      @update:model-value="onPickerChange"
    >
      <template #input>
        <UiInput
          v-if="readonly && elements.input"
          v-model="input"
          class="readonly-input"
          align="center"
          :placeholder="placeholder"
          readonly="button"
        >
          <template #postfix>
            <InputButton
              v-if="elements.calendar && !disabled && !dateRangePickerAvailable"
              side="right"
              tabindex="-1"
            >
              <CalendarIcon />
            </InputButton>
          </template>
        </UiInput>

        <UiButton
          v-else
          mode="outline"
          color="black"
          shape="square"
          :disabled="disabled"
          tabindex="-1"
        >
          <CalendarIcon v-if="readonly" />
          <CalendarPlusIcon v-else />
        </UiButton>
      </template>
    </DateRangePicker>
  </div>
</template>

<style scoped lang="scss">
.ui-date {
  display: flex;
  min-width: 0;
  gap: 4px;

  &._picker-input {
    .vue-daterange-picker {
      flex: 1 1 100%;
    }
  }

  &._drops-up :deep(.daterangepicker) {
    top: auto;
    bottom: 100%;
    margin-top: 0;
    margin-bottom: 7px;

    &:before,
    &:after {
      top: auto;
      transform: rotate(180deg);
    }

    &:before {
      bottom: -7px;
    }

    &:after {
      bottom: -6px;
    }
  }
}

.chevron-icon {
  fill: var(--color-gray-600);

  &._left {
    transform: rotate(90deg);
  }

  &._right {
    transform: rotate(-90deg);
  }
}

.ui-button,
.vue-daterange-picker {
  flex-shrink: 0;
}
</style>
